import { Constructor } from '@angular/cdk/table';
import { Injectable } from '@angular/core';
import { UserClaims } from '@okta/okta-auth-js';
import { BehaviorSubject, filter, map, ReplaySubject, take } from 'rxjs';
import { firstValueOrNull } from 'src/util/rxjs/observables/first-value';
import { uuid } from 'src/util/uuid';

export interface LoginOptions {
  redirectUrl?: string;
}
export interface LogoutOptions {
  alertText?: string;
  redirectUrl?: string;
}

interface Event<T> {
  id: string;
  payload?: T;
}
class EventResponse<T> implements Event<T> {
  constructor(public id: string, public payload?: T) {}
}
export class LoginEvent implements Event<LoginOptions> {
  constructor(public id: string, public payload?: LoginOptions) {}
}
export class LogoutEvent implements Event<LogoutOptions> {
  constructor(public id: string, public payload?: LogoutOptions) {}
}
export class ImpersonateUserEvent implements Event<string> {
  constructor(public id: string, public payload: string) {}
}
export class StopImpersonatingEvent implements Event<void> {
  constructor(public id: string) {}
}
export class LoginEventResponse extends EventResponse<Promise<void>> {}
export class LogoutEventResponse extends EventResponse<Promise<void>> {}
export class ImpersonateUserEventResponse extends EventResponse<Promise<void>> {}
export class StopImpersonatingEventResponse extends EventResponse<void> {}

@Injectable({
  providedIn: 'root',
})
export class AuthProxyService {
  private _accessToken$ = new ReplaySubject<string | undefined>(1);
  private _claims$ = new ReplaySubject<UserClaims | undefined>(1);
  private _impersonatedUserId$ = new ReplaySubject<string | undefined>(1);
  private _events$ = new BehaviorSubject<Event<any> | undefined>(undefined);

  public get accessToken$() {
    return this._accessToken$.asObservable();
  }

  public get claims$() {
    return this._claims$.asObservable();
  }

  public get impersonatedUserId$() {
    return this._impersonatedUserId$.asObservable();
  }

  public get events$() {
    return this._events$.asObservable();
  }

  public setClaims(claims: UserClaims | undefined) {
    this._claims$.next(claims);
  }

  public setAccessToken(token: string | undefined) {
    this._accessToken$.next(token);
  }

  public setImpersonatedUserId(id: string | undefined) {
    this._impersonatedUserId$.next(id);
  }

  public sendEvent<T extends Event<any>>(event: T) {
    this._events$.next(event);
  }

  public async impersonateUser(userId: string) {
    const impersonate = new ImpersonateUserEvent(uuid(), userId);
    return this._processEvent(impersonate, ImpersonateUserEventResponse);
  }

  public async stopImpersonating() {
    const stopImpersonating = new StopImpersonatingEvent(uuid());
    return this._processEvent(stopImpersonating, StopImpersonatingEventResponse);
  }

  public async login(options?: LoginOptions) {
    const login = new LoginEvent(uuid(), options);
    return this._processEvent(login, LoginEventResponse);
  }

  public async logout(options?: LogoutOptions) {
    const logout = new LogoutEvent(uuid(), options);
    return this._processEvent(logout, LogoutEventResponse);
  }

  private async _processEvent<T>(event: Event<any>, responseType: Constructor<Event<T>>) {
    return new Promise<T>((res, rej) => {
      // listen for the response
      firstValueOrNull(
        this._events$.pipe(
          filter((evt) => evt?.id === event.id && evt instanceof responseType),
          map((m) => (m as InstanceType<typeof responseType>).payload),
          take(1),
        ),
      )
        .then((r) => res(r as T))
        .catch((e) => rej(e));
      // send the event
      this._events$.next(event);
    });
  }
}
